import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, MessageCircle, Loader, AlertCircle, CheckCircle2, Check } from 'lucide-react';
import authVerificationService from '../services/authVerificationService';
import CountryCodeSelect from './CountryCodeSelect';

// ─────────────────────────────────────────────────────────────────────────────
// WhatsappVerificationModal — replica del flow Flutter V2.
//
// Dos fases:
//   1. SEND: usuaria confirma su número (enmascarado) y dispara el envío del
//            código por WhatsApp (`send_whatsapp_verification_code`).
//   2. CODE: input de PIN 4 dígitos con auto-focus y auto-submit al completar.
//            Cooldown de 60s antes de permitir reenvío.
//
// Estilos alineados al design system Daisies:
//   - rounded-card / rounded-input
//   - shadow-soft / shadow-elevated
//   - press-scale / press-scale-card
//   - text-text-primary / text-text-secondary
//   - bg-daisies / bg-daisies-50 / bg-daisies-hover
// ─────────────────────────────────────────────────────────────────────────────

const PIN_LENGTH = 4;
const RESEND_COOLDOWN_SECONDS = 60;

const maskPhone = (rawPhone) => {
  if (!rawPhone || typeof rawPhone !== 'string') return '';
  const digits = rawPhone.replace(/\D/g, '');
  if (digits.length < 4) return rawPhone;
  const last4 = digits.slice(-4);
  return `••• ••• ${last4}`;
};

// country_phone_id de México en Odoo (res.country). El alta de Daisies es MX-first.
const MX_COUNTRY_PHONE_ID = 156;

const WhatsappVerificationModal = ({
  userId,
  authToken,
  phone,
  onVerified,
  onCancel,
  onPhoneChanged,
  sendTitle,
  sendSubtitle,
}) => {
  const [phase, setPhase] = useState('send'); // 'send' | 'code'
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [notificationId, setNotificationId] = useState(null);
  const [pin, setPin] = useState(['', '', '', '']);
  const [resendCountdown, setResendCountdown] = useState(0);

  // ─── Cambio de número (igual que ChangePhoneNumberPage en Flutter) ───
  const [currentPhone, setCurrentPhone] = useState(phone || '');
  // Si la usuaria aún no tiene número (perfil incompleto), abrimos directo el
  // formulario para agregarlo; si ya lo tiene, partimos del paso de envío.
  const [editing, setEditing] = useState(() => !String(phone || '').trim());
  const [newPhone, setNewPhone] = useState('');
  const [newCountry, setNewCountry] = useState(null); // objeto país {id, code, phone_code}
  const [phoneError, setPhoneError] = useState('');
  const [changingPhone, setChangingPhone] = useState(false);

  // Si el prop cambia (p. ej. el padre refresca al usuario), sincronizamos.
  useEffect(() => { setCurrentPhone(phone || ''); }, [phone]);

  const maskedPhone = useMemo(() => maskPhone(currentPhone), [currentPhone]);
  const inputRefs = useRef([]);

  // ─── Cooldown del reenvío ───
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const t = setTimeout(() => setResendCountdown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCountdown]);

  // ─── Auto-focus al entrar a la fase de PIN ───
  useEffect(() => {
    if (phase === 'code') {
      setTimeout(() => inputRefs.current[0]?.focus(), 80);
    }
  }, [phase]);

  // ─── Enviar código ───
  const handleSendCode = async () => {
    setLoading(true);
    setErrorMsg('');
    const res = await authVerificationService.sendWhatsappVerificationCode({
      authToken,
      userId,
    });
    setLoading(false);
    if (!res.success) {
      setErrorMsg(res.error || 'No se pudo enviar el código.');
      return;
    }
    setNotificationId(res.notificationId);
    setPin(['', '', '', '']);
    setPhase('code');
    setResendCountdown(RESEND_COOLDOWN_SECONDS);
  };

  // ─── Cambiar número ───
  const handleStartEdit = () => {
    setEditing(true);
    setNewPhone('');
    setPhoneError('');
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setNewPhone('');
    setPhoneError('');
  };

  const handleChangePhone = async () => {
    const clean = newPhone.replace(/\D/g, '');
    const isMx = !newCountry || newCountry.code === 'MX';
    if (isMx ? !/^\d{10}$/.test(clean) : clean.length < 7 || clean.length > 15) {
      setPhoneError(isMx ? 'Ingresa un número de 10 dígitos válido.' : 'Ingresa un número válido.');
      return;
    }
    setChangingPhone(true);
    setPhoneError('');
    const res = await authVerificationService.changeUserPhone({
      authToken,
      userId,
      mobile: clean,
      countryPhoneId: newCountry?.id || MX_COUNTRY_PHONE_ID,
    });
    setChangingPhone(false);
    if (!res.success) {
      setPhoneError(res.error || 'No se pudo actualizar el número. Intenta de nuevo.');
      return;
    }
    // Cambiar el número invalida la verificación: volvemos a la fase de envío
    // con el nuevo número para que la usuaria mande el código a ese WhatsApp.
    setCurrentPhone(clean);
    setEditing(false);
    setNewPhone('');
    setErrorMsg('');
    onPhoneChanged?.(clean);
  };

  // ─── Validar código ───
  const submitCode = async (codeArr) => {
    const code = codeArr.join('');
    if (code.length < PIN_LENGTH) return;
    setLoading(true);
    setErrorMsg('');
    const res = await authVerificationService.validateWhatsappVerificationCode({
      authToken,
      userId,
      code,
      notificationId,
    });
    setLoading(false);
    if (!res.success) {
      setErrorMsg(res.error || 'Código incorrecto. Intenta de nuevo.');
      setPin(['', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
      return;
    }
    onVerified?.({
      phoneVerified: res.phoneVerified,
      stepState: res.stepState,
    });
  };

  // ─── Handlers de cada celda del PIN ───
  const handlePinChange = (idx, value) => {
    // Solo dígitos
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...pin];
    next[idx] = digit;
    setPin(next);
    if (errorMsg) setErrorMsg('');

    if (digit && idx < PIN_LENGTH - 1) {
      inputRefs.current[idx + 1]?.focus();
    }

    if (next.every((d) => d.length === 1)) {
      submitCode(next);
    }
  };

  const handlePinKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !pin[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePinPaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData)?.getData('text') || '';
    const digits = text.replace(/\D/g, '').slice(0, PIN_LENGTH).split('');
    if (digits.length === 0) return;
    const next = ['', '', '', ''];
    digits.forEach((d, i) => { next[i] = d; });
    setPin(next);
    if (digits.length === PIN_LENGTH) {
      submitCode(next);
    } else {
      inputRefs.current[digits.length]?.focus();
    }
  };

  // ─── Reenvío ───
  const handleResend = async () => {
    if (resendCountdown > 0) return;
    await handleSendCode();
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-end md:items-center justify-center p-0 md:p-4 overflow-y-auto animate-fadeIn">
      <div className="bg-white rounded-t-card md:rounded-card shadow-elevated w-full md:max-w-md max-h-[95vh] md:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="relative px-6 md:px-7 pt-6 md:pt-7 pb-5 bg-brand-50">
          <button
            onClick={onCancel}
            className="absolute top-5 right-5 text-text-secondary hover:text-text-primary transition-colors"
            aria-label="Cerrar"
            disabled={loading}
          >
            <X size={24} />
          </button>
          <div className="flex items-start gap-4 pr-10">
            <div className="p-3 bg-[#25D366]/15 text-[#25D366] rounded-input flex-shrink-0">
              <MessageCircle size={26} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl md:text-2xl font-bold text-text-primary leading-tight">
                {phase === 'send' ? 'Verifica tu WhatsApp' : 'Ingresa el código'}
              </h3>
              <p className="text-sm md:text-base text-text-secondary mt-2 leading-relaxed">
                {phase === 'send'
                  ? 'Para confirmar tu cita necesitamos verificar tu número.'
                  : `Enviamos un código de 4 dígitos a ${maskedPhone || 'tu WhatsApp'}.`}
              </p>
            </div>
          </div>
        </div>

        {/* Cuerpo */}
        <div className="flex-1 overflow-y-auto px-6 md:px-7 py-6">
          {phase === 'send' && (
            <SendPhase
              maskedPhone={maskedPhone}
              loading={loading}
              errorMsg={errorMsg}
              onSend={handleSendCode}
              editing={editing}
              newPhone={newPhone}
              newCountry={newCountry}
              phoneError={phoneError}
              changingPhone={changingPhone}
              onStartEdit={handleStartEdit}
              onCancelEdit={handleCancelEdit}
              onChangePhone={handleChangePhone}
              onNewCountryChange={(country) => { setNewCountry(country); setNewPhone(''); setPhoneError(''); }}
              onNewPhoneChange={(val) => {
                const max = !newCountry || newCountry.code === 'MX' ? 10 : 15;
                setNewPhone(val.replace(/\D/g, '').slice(0, max));
                setPhoneError('');
              }}
            />
          )}

          {phase === 'code' && (
            <CodePhase
              pin={pin}
              inputRefs={inputRefs}
              loading={loading}
              errorMsg={errorMsg}
              resendCountdown={resendCountdown}
              onPinChange={handlePinChange}
              onPinKeyDown={handlePinKeyDown}
              onPinPaste={handlePinPaste}
              onResend={handleResend}
              onBackToSend={() => { setPhase('send'); setErrorMsg(''); }}
            />
          )}
        </div>

        {/* Cancelar */}
        <div className="px-6 md:px-7 pb-6 pt-1 border-t border-brand-100">
          <button
            onClick={onCancel}
            disabled={loading}
            className="press-scale w-full py-3 text-text-secondary text-sm font-semibold rounded-full hover:bg-surface transition-all disabled:opacity-50"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Fase 1: enviar código ───
const SendPhase = ({
  maskedPhone,
  loading,
  errorMsg,
  onSend,
  editing,
  newPhone,
  newCountry,
  phoneError,
  changingPhone,
  onStartEdit,
  onCancelEdit,
  onChangePhone,
  onNewCountryChange,
  onNewPhoneChange,
}) => (
  <div className="space-y-5">
    <div className="bg-surface/60 rounded-card p-4">
      <p className="ds-caption text-text-secondary uppercase tracking-wider mb-1">Tu número</p>
      <p className="text-lg font-semibold text-text-primary tabular-nums">
        {maskedPhone || 'Sin número registrado'}
      </p>
    </div>

    <ul className="space-y-2.5 text-sm text-text-secondary">
      <li className="flex items-start gap-2">
        <CheckCircle2 size={16} className="text-daisies mt-0.5 flex-shrink-0" />
        <span>Te llegará un mensaje de WhatsApp con un código de 4 dígitos.</span>
      </li>
      <li className="flex items-start gap-2">
        <CheckCircle2 size={16} className="text-daisies mt-0.5 flex-shrink-0" />
        <span>Solo se verifica una vez — luego podrás agendar libremente.</span>
      </li>
    </ul>

    {errorMsg && (
      <div className="flex items-start gap-2 p-3 bg-red-50 rounded-input">
        <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-red-600">{errorMsg}</p>
      </div>
    )}

    {editing ? (
      /* Formulario inline para cambiar el número (réplica de ChangePhoneNumberPage) */
      <div className="bg-surface/60 rounded-card p-4 space-y-3">
        <p className="text-sm font-semibold text-text-primary">Nuevo número de teléfono</p>
        <div className="flex gap-2">
          <CountryCodeSelect
            value={newCountry?.code}
            onChange={onNewCountryChange}
            disabled={changingPhone}
            error={!!phoneError}
          />
          <input
            type="tel"
            inputMode="numeric"
            maxLength={!newCountry || newCountry.code === 'MX' ? 10 : 15}
            value={newPhone}
            onChange={(e) => onNewPhoneChange(e.target.value)}
            placeholder={!newCountry || newCountry.code === 'MX' ? '10 dígitos' : 'Número'}
            disabled={changingPhone}
            autoFocus
            className="flex-1 min-w-0 px-3 py-2.5 rounded-input bg-warm-canvas text-base text-text-primary focus:ring-2 focus:ring-brand focus:outline-none transition-all disabled:opacity-50"
          />
        </div>
        {phoneError && <p className="text-sm text-red-600">{phoneError}</p>}
        <div className="flex gap-2 pt-1">
          <button
            onClick={onCancelEdit}
            disabled={changingPhone}
            className="press-scale flex-1 py-2.5 rounded-full border-[1.5px] border-brand text-daisies text-sm font-semibold hover:bg-daisies/5 transition-all disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            <X size={15} />
            Cancelar
          </button>
          <button
            onClick={onChangePhone}
            disabled={changingPhone || newPhone.length < 10}
            className="press-scale-card flex-1 py-2.5 rounded-full bg-daisies text-white text-sm font-bold hover:bg-daisies-hover transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
          >
            {changingPhone ? (
              <>
                <Loader size={15} className="animate-spin" />
                Guardando…
              </>
            ) : (
              <>
                <Check size={15} />
                Guardar número
              </>
            )}
          </button>
        </div>
      </div>
    ) : (
      <>
        <button
          onClick={onSend}
          disabled={loading}
          className="press-scale-card w-full px-5 py-3.5 bg-daisies text-white rounded-full font-bold text-base hover:bg-daisies-hover transition-all disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader size={18} className="animate-spin" />
              Enviando…
            </>
          ) : (
            <>
              <MessageCircle size={18} />
              Enviar código por WhatsApp
            </>
          )}
        </button>

        <button
          onClick={onStartEdit}
          disabled={loading}
          className="press-scale w-full py-2 text-daisies hover:text-daisies-hover text-sm font-bold transition-colors disabled:opacity-50"
        >
          Cambiar mi número
        </button>
      </>
    )}
  </div>
);

// ─── Fase 2: ingresar código ───
const CodePhase = ({
  pin,
  inputRefs,
  loading,
  errorMsg,
  resendCountdown,
  onPinChange,
  onPinKeyDown,
  onPinPaste,
  onResend,
  onBackToSend,
}) => (
  <div className="space-y-5">
    {/* Inputs PIN */}
    <div className="flex justify-center gap-3" onPaste={onPinPaste}>
      {pin.map((digit, idx) => (
        <input
          key={idx}
          ref={(el) => (inputRefs.current[idx] = el)}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={digit}
          onChange={(e) => onPinChange(idx, e.target.value)}
          onKeyDown={(e) => onPinKeyDown(idx, e)}
          disabled={loading}
          className={`w-14 h-16 text-center text-2xl font-bold tabular-nums rounded-input transition-all focus:outline-none focus:ring-2 focus:ring-brand ${
            errorMsg
              ? 'ring-2 ring-red-400 bg-red-50/50 text-red-600'
              : digit
                ? 'ring-2 ring-brand bg-daisies-50 text-daisies'
                : 'bg-warm-canvas text-text-primary'
          } disabled:opacity-60`}
        />
      ))}
    </div>

    {errorMsg && (
      <div className="flex items-start gap-2 p-3 bg-red-50 rounded-input">
        <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-red-600">{errorMsg}</p>
      </div>
    )}

    {loading && (
      <div className="flex items-center justify-center gap-2 text-sm text-text-secondary">
        <Loader size={16} className="animate-spin" />
        Validando código…
      </div>
    )}

    {/* Reenvío */}
    <div className="flex flex-col items-center gap-2 pt-2">
      <button
        onClick={onResend}
        disabled={resendCountdown > 0 || loading}
        className={`text-sm font-semibold transition-colors ${
          resendCountdown > 0 || loading
            ? 'text-text-secondary cursor-not-allowed'
            : 'text-daisies hover:text-daisies-hover'
        }`}
      >
        {resendCountdown > 0
          ? `Reenviar en ${resendCountdown}s`
          : 'Reenviar código'}
      </button>
      <button
        onClick={onBackToSend}
        disabled={loading}
        className="ds-caption text-text-secondary hover:text-text-primary font-medium uppercase tracking-wider transition-colors disabled:opacity-50"
      >
        ¿Número incorrecto?
      </button>
    </div>
  </div>
);

export default WhatsappVerificationModal;
